import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { BASE_URL } from 'configs'
import { toast } from 'react-toastify'
import { setIsLoading } from 'features/loader/loader'
import { store } from 'store'
// eslint-disable-next-line import/no-cycle
import authService from './auth.service'

declare module 'axios' {
  export interface AxiosRequestConfig {
    hideLoader: boolean
    skipAuthRefresh?: boolean
  }
}

// Token refresh flag and queue
let isRefreshing = false
let refreshSubscribers: ((token: string | null) => void)[] = []

// Configure axios to send credentials
axios.defaults.withCredentials = true

// Function to process the queue with new token
const processQueue = (token: string | null) => {
  refreshSubscribers.forEach((callback) => callback(token))
  refreshSubscribers = []
}

const decodeJWT = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch (e) {
    return null
  }
}

const isTokenValid = (token: string) => {
  if (!token) return false
  try {
    const decoded = decodeJWT(token)
    if (!decoded || !decoded.exp) return false
    return decoded.exp * 1000 > Date.now()
  } catch (e) {
    return false
  }
}

const isTokenExpiringSoon = (token) => {
  if (!token) return true

  const payload = decodeJWT(token)
  if (!payload || !payload.exp) return true

  const expTime = payload.exp * 1000 // Convert to milliseconds
  const currentTime = Date.now()
  const bufferTime = 120000 // 2 minutes buffer
  const timeUntilExpiry = expTime - currentTime

  // Refresh if the token is within buffer time OR already expired
  return timeUntilExpiry <= bufferTime
}

const refreshAccessToken = async () => {
  if (isRefreshing) {
    // Return a promise that resolves when the refresh is complete
    return new Promise<string | null>((resolve) => {
      refreshSubscribers.push((token) => {
        resolve(token)
      })
    })
  }

  try {
    isRefreshing = true
    const token = await authService.refreshToken()
    const accessToken = token?.data?.data.token
    if (accessToken) {
      localStorage.setItem('loginAuthToken', accessToken)
      processQueue(accessToken)
      return accessToken
    }
    processQueue(null)
    return null
  } catch (error) {
    console.error('Token refresh failed', error)
    processQueue(null)
    return null
  } finally {
    isRefreshing = false
  }
}

const getFullUrl = (url: string) => {
  if (url.startsWith('http')) {
    return url
  }
  return BASE_URL + url
}

axios.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    // Skip token validation for authentication and captcha endpoints
    if (
      config.url.includes('/authentication') ||
      config.url.includes('/get-captcha-details-admin') ||
      config.url.includes('/refresh-token') ||
      config.skipAuthRefresh
    ) {
      if (config.url) {
        config.url = getFullUrl(config.url)
      }
      return config
    }

    // For all other endpoints, handle token validation
    let token = localStorage.getItem('loginAuthToken')

    // If we have a token, validate it
    if (token) {
      // Check if token is valid
      if (!isTokenValid(token)) {
        // Token is invalid, try to refresh
        const newToken = await refreshAccessToken()
        if (!newToken) {
          // If refresh failed, redirect to login
          localStorage.removeItem('loginAuthToken')
          if (!window.location.pathname.includes('/')) {
            window.location.href = '/'
          }
          return Promise.reject(new Error('Invalid token'))
        }
        token = newToken
      }
      // If token is valid but expiring soon, refresh in background
      else if (isTokenExpiringSoon(token)) {
        refreshAccessToken().then((newToken) => {
          if (newToken) {
            token = newToken
          }
        })
      }
    }

    // Add token to headers if we have one
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    if (config.url) {
      config.url = getFullUrl(config.url)
    }

    if (config.url) {
      config.headers['Cache-Control'] =
        'no-cache, no-store, must-revalidate, post-check=0, pre-check=0'
      config.headers.Pragma = 'no-cache'
      config.headers.Expires = '0'
    }

    if (!config.hideLoader) {
      store.dispatch(setIsLoading(true))
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    store.dispatch(setIsLoading(false))
    return response
  },
  async (error: AxiosError) => {
    store.dispatch(setIsLoading(false))
    let errorMessage = 'An error occurred'

    // Handle unauthorized/forbidden errors
    if (error.response?.status === 401 || error.response?.status === 403) {
      const originalRequest = error.config

      // Prevent infinite refresh loops
      if (originalRequest.skipAuthRefresh) {
        throw error
      }

      try {
        // Wait for token refresh
        const newToken = await refreshAccessToken()
        if (newToken) {
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          originalRequest.skipAuthRefresh = true // Prevent infinite loops
          return axios(originalRequest)
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
      }

      // If we get here, refresh failed
      localStorage.clear()
      window.location.href = '/'
      return Promise.reject(error)
    }

    // Handle other error messages
    if (error.response?.data?.responseException?.exceptionMessage) {
      errorMessage =
        error.response?.data?.responseException?.exceptionMessage.message !==
        undefined
          ? error.response?.data?.responseException?.exceptionMessage.message
          : error.response?.data?.responseException?.exceptionMessage
    }

    if (
      !error.response?.request?.responseURL.includes('register-user') &&
      !error.response?.request?.responseURL.includes('add-fastabs') &&
      !error.response?.request?.responseURL.includes(
        'get-total-amount-by-order-id'
      )
    ) {
      toast.error(errorMessage)
    }

    return Promise.reject(error)
  }
)

export default axios
